import React from 'react';

import {
    Box,
    Checkbox,
    FormControlLabel,
    styled,
    useTheme,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { UserRole } from 'business/definitions';
import { useSelector } from 'store/hooks';
import { MODULES_TRANSLATION_PATH } from './helpers/constants';
import { Permission, RoleTreeWithPermissions } from './types';
import SubRoleSwitch from './ui/SubRoleSwitch';

const StyPaper = styled(Paper)(({ theme }) => ({
    flexDirection: 'column',
    display: 'flex',
    padding: theme.spacing(1),
}));

const StyPermissionCheckBoxContainer = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    marginLeft: theme.spacing(5),
}));

type Props = {
    addRole: (r: UserRole) => void;
    removeRole: (r: UserRole) => void;
    userRoles: UserRole[];
    roleTree: RoleTreeWithPermissions;
};

/**
 * A component in charge of displaying the logic for setting one simple user subrole with permissions (like "Administration du module budget")
 * Here, contrary to SimpleRoleCard.tsx, the subrole is not binded with an actual user.roles. It's only a header that group some actions on permissions.
 * Permissions are the actual user.roles in that case.
 */
const SubRoleCardWithPermission = (props: Props): JSX.Element => {
    const { userRoles, roleTree, addRole, removeRole } = props;
    const { permissions } = roleTree;
    const groupedPermissions = permissions
        .filter((permission) => permission.isControlledByHeader)
        .map((permission) => permission.permissionName);
    const conditionDisplayPermissions = permissions
        .filter((permission) => permission.determinePermissionsDisplay)
        .map((permission) => permission.permissionName);

    const currentUserRoles = useSelector((state) => state.vision.user.roles);
    const theme = useTheme();
    const { t } = useTranslation();

    const removeAllPermissions = () => {
        permissions.forEach((permission) => {
            removeRole(permission.permissionName);
        });
    };

    const addAllPermissions = () => {
        groupedPermissions.forEach((permissionName) => {
            addRole(permissionName);
        });
    };

    const isHeadingRoleSelected = () =>
        groupedPermissions.some((permissionName) =>
            userRoles.includes(permissionName),
        );

    const handleChangeHeadingRole = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => (event.target.checked ? addAllPermissions() : removeAllPermissions());

    const handleChangePermission = (
        event: React.ChangeEvent<HTMLInputElement>,
        permission: Permission,
    ) =>
        event.target.checked
            ? addRole(permission.permissionName)
            : removeRole(permission.permissionName);

    const checkCurrentUserPermissionAccess = (permission: Permission) =>
        currentUserRoles.some((role) => permission.visibleBy.includes(role));

    const arePermissionsDisplayed = () =>
        userRoles.some((role) => conditionDisplayPermissions.includes(role));

    return (
        <StyPaper>
            <SubRoleSwitch
                roleTree={roleTree}
                isSwitchSelected={isHeadingRoleSelected}
                handleSwitchEvent={handleChangeHeadingRole}
            />

            {arePermissionsDisplayed() &&
                permissions.map(
                    (permission) =>
                        checkCurrentUserPermissionAccess(permission) && (
                            <StyPermissionCheckBoxContainer
                                key={permission.permissionName}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={userRoles.includes(
                                                permission.permissionName,
                                            )}
                                            onChange={(event) =>
                                                handleChangePermission(
                                                    event,
                                                    permission,
                                                )
                                            }
                                        />
                                    }
                                    label={t(
                                        `${MODULES_TRANSLATION_PATH}.titles.${permission.permissionName}`,
                                    )}
                                />
                                <Tooltip
                                    title={t(
                                        `${MODULES_TRANSLATION_PATH}.descriptions.${permission.permissionName}`,
                                    )}
                                >
                                    <InfoIcon
                                        fontSize="medium"
                                        sx={{
                                            color: theme.palette.primary.main,
                                            margin: `0 ${theme.spacing(1)}`,
                                        }}
                                    />
                                </Tooltip>
                            </StyPermissionCheckBoxContainer>
                        ),
                )}
        </StyPaper>
    );
};

export default SubRoleCardWithPermission;
